/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle, Upload, Download, RotateCcw } from 'lucide-react';
import useBulkAddClients from '@/hooks/useBulkAddClients';
import LoadingModal from '../LoadingModal';

interface ImportSummaryProps {
  data: any[];
  filename: string;
  onNewImport: () => void;
  onBackToList: () => void;
}

export function ImportSummary({
  data,
  filename,
  onNewImport,
  onBackToList,
}: ImportSummaryProps) {
  const { mutate: bulkAdd, isPending } = useBulkAddClients();

  const validRows = data.filter(
    (row) => row['First Name'] && row['Last Name'] && row['Email Address 1'],
  );

  const invalidRows = data.filter(
    (row) => !row['First Name'] || !row['Last Name'] || !row['Email Address 1'],
  );

  const fieldStats = Object.keys(data[0] || {}).map((field) => {
    const filled = data.filter(
      (row) => row[field] && row[field].toString().trim(),
    ).length;
    return {
      field,
      filled,
      percentage: Math.round((filled / data.length) * 100),
    };
  });

  const downloadProcessedData = () => {
    const csvContent = [
      Object.keys(data[0] || {}).join(','),
      ...validRows.map((row) =>
        Object.values(row)
          .map((value) => `"${String(value || '').replace(/"/g, '""')}"`)
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processed_${filename}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    bulkAdd(
      data.map((d) => ({
        firstName: d['First Name'],
        lastName: d['Last Name'],
        emailAddress: d['Email Address 1'],
        coOwnerFirstName: d['Co-Owner First Name'],
        coOwnerLastName: d['Co-Owner Last Name'],
        emailAddress2: d['Email Address 2'],
        phoneNumber: d['Telephone'],
        addressLine1: d['Street Address'],
        addressLine2: d['Address Line 2'],
        city: d['City'],
        state: d['State'],
        zipCode: d['Zip Code'],
      })),
      {
        onSuccess: () => {
          onBackToList();
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <LoadingModal show={isPending} messages={['Importing data...']} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-success" />
            Import Complete
          </CardTitle>
          <CardDescription>
            Your data has been successfully processed and is ready for import.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-lg bg-success/10">
              <div className="text-3xl font-bold text-success">
                {validRows.length}
              </div>
              <div className="text-sm text-muted-foreground">Valid Records</div>
            </div>
            <div className="text-center p-6 rounded-lg bg-warning/10">
              <div className="text-3xl font-bold text-warning">
                {invalidRows.length}
              </div>
              <div className="text-sm text-muted-foreground">
                Invalid Records
              </div>
            </div>
            <div className="text-center p-6 rounded-lg bg-primary/10">
              <div className="text-3xl font-bold text-primary">
                {data.length}
              </div>
              <div className="text-sm text-muted-foreground">Total Records</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Field Coverage Analysis</CardTitle>
          <CardDescription>
            Shows how much data is available for each field
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {fieldStats.map((stat) => (
              <div
                key={stat.field}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium min-w-48">{stat.field}</span>
                  <div className="flex-1 bg-muted rounded-full h-2 min-w-32">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{ width: `${stat.percentage}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {stat.filled}/{data.length}
                  </span>
                  <Badge
                    variant={
                      stat.percentage >= 80
                        ? 'default'
                        : stat.percentage >= 50
                        ? 'secondary'
                        : 'outline'
                    }
                  >
                    {stat.percentage}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {invalidRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Invalid Records</CardTitle>
            <CardDescription>
              Records missing required fields (First Name, Last Name, or Email
              Address 1)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>First Name</TableHead>
                      <TableHead>Last Name</TableHead>
                      <TableHead>Email Address 1</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invalidRows.slice(0, 10).map((row, index) => {
                      const issues = [];
                      if (!row['First Name']) issues.push('Missing First Name');
                      if (!row['Last Name']) issues.push('Missing Last Name');
                      if (!row['Email Address 1']) issues.push('Missing Email');

                      return (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{row['First Name'] || '-'}</TableCell>
                          <TableCell>{row['Last Name'] || '-'}</TableCell>
                          <TableCell>{row['Email Address 1'] || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="destructive" className="text-xs">
                              {issues.join(', ')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            {invalidRows.length > 10 && (
              <div className="text-center text-sm text-muted-foreground mt-4">
                Showing 10 of {invalidRows.length} invalid records
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onNewImport}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Import Another File
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={downloadProcessedData}>
            <Download className="w-4 h-4 mr-2" />
            Download Processed Data
          </Button>
          <Button onClick={handleImport} disabled={isPending || !validRows}>
            <Upload className="w-4 h-4 mr-2" />
            Import to Database
          </Button>
        </div>
      </div>
    </div>
  );
}
